import { useState } from "react";
import { ExternalLink, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { cn } from "@/lib/utils";
import { ErpSidebarContent } from "./ErpSidebarContent";
import { useAuthStore } from "@/lib/store/authStore";
import { useCustomDomain } from "@/features/storefront/settings/useCustomDomain";
import { useLineMessagingConfig } from "@/features/storefront/settings/useLineMessagingConfig";
import { designTokens } from "@/config/design-tokens";

export const RAIL_WIDTH = 70; // Desk reference spec (nav-left-side)
export const PANEL_WIDTH = 250;
export const SIDEBAR_EXPANDED_WIDTH = RAIL_WIDTH + PANEL_WIDTH;
export const SIDEBAR_COLLAPSED_WIDTH = RAIL_WIDTH;
export const SIDEBAR_EXPANDED_OFFSET = SIDEBAR_EXPANDED_WIDTH;
export const SIDEBAR_COLLAPSED_OFFSET = SIDEBAR_COLLAPSED_WIDTH;

interface ErpSidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
}

/**
 * ERP sidebar — panel + toggle only.
 * The app rail is now rendered globally via AppRailFixed in AppShell.
 */
/** Strip " - {10-char tenant ID}" suffix from company display name */
const TENANT_ID_SUFFIX_RE = /\s-\s[a-z0-9]{10}$/;
function stripTenantSuffix(name: string | null): string | null {
  return name ? name.replace(TENANT_ID_SUFFIX_RE, "") : name;
}

/**
 * Company avatar for the sidebar header.
 * Shows the connected LINE OA profile picture (most tenants connect one);
 * falls back to a single-letter initial of the company name when there is no
 * avatar — or if the OA image fails to load.
 */
function CompanyAvatar({ src, name }: { src: string | null; name: string }) {
  const [errored, setErrored] = useState(false);
  const initial = (name.trim().charAt(0) || "?").toUpperCase();

  if (src && !errored) {
    return (
      <img
        src={src}
        alt={name}
        onError={() => setErrored(true)}
        className="w-9 h-9 rounded object-cover shrink-0 border border-black/[0.06] dark:border-white/[0.08]"
      />
    );
  }

  return (
    <div className="w-9 h-9 rounded bg-gray-100 dark:bg-gray-800 shrink-0 flex items-center justify-center text-[14px] font-medium text-gray-500 dark:text-gray-400 select-none">
      {initial}
    </div>
  );
}

export function ErpSidebar({ collapsed, onToggleCollapse }: ErpSidebarProps) {
  const rawCompany = useAuthStore((state) => state.tenantCompany);
  const tenantCompany = stripTenantSuffix(rawCompany);
  const defaultStorefrontUrl = useAuthStore((state) => state.storefrontUrl);
  const { domain: customDomain, status: domainStatus, isLoading: isDomainLoading } = useCustomDomain();
  const storefrontUrl = isDomainLoading ? null
    : domainStatus === "active" && customDomain
      ? `https://${customDomain}`
      : defaultStorefrontUrl;

  // Most tenants connect a LINE OA — use its avatar as the company logo,
  // falling back to a one-letter initial. Hook is store-scoped + 5min stale
  // (built for sidebar use, see useLineMessagingConfig).
  const { status: lineStatus } = useLineMessagingConfig();
  const oaAvatarUrl =
    lineStatus?.connected && lineStatus.bot_picture_url ? lineStatus.bot_picture_url : null;

  return (
    <>
      {/* Expand button — in the rail, visible only when collapsed */}
      {collapsed && (
        <button
          type="button"
          onClick={onToggleCollapse}
          className={cn(
            "fixed z-30 flex items-center justify-center w-10 h-8 rounded-sm",
            "text-gray-400 hover:text-gray-600 hover:bg-gray-100/50",
            "transition-all duration-150"
          )}
          style={{
            left: (RAIL_WIDTH - 40) / 2,
            bottom: 60,
          }}
          aria-label="ขยายเมนู"
          title="ขยายเมนู"
        >
          <PanelLeftOpen className="w-4 h-4" />
        </button>
      )}

      {/* Menu panel — flush sidebar next to rail */}
      <nav
        aria-label="ธุรกิจ เมนู"
        className="fixed z-30 overflow-hidden flex flex-col transition-[opacity,transform] duration-300 ease-out"
        style={{
          width: `${PANEL_WIDTH}px`,
          top: 0,
          left: `${RAIL_WIDTH}px`,
          bottom: 0,
          background: "#F9FAFB",
          borderRight: designTokens.borders.sidebar,
          opacity: collapsed ? 0 : 1,
          transform: collapsed ? "translateX(-8px)" : "translateX(0)",
          pointerEvents: collapsed ? "none" : "auto",
        }}
      >
        {/* Company info */}
        <div className="flex items-center gap-3 px-4 pt-5 pb-3 shrink-0">
          {/* Company logo — LINE OA avatar, else one-letter initial */}
          <CompanyAvatar src={oaAvatarUrl} name={tenantCompany || "My Company"} />
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-normal text-gray-900 dark:text-gray-100 truncate leading-snug">
              {tenantCompany || "My Company"}
            </p>
            {storefrontUrl && (
              <a
                href={storefrontUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-[11px] text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 truncate leading-snug mt-0.5 transition-colors"
              >
                <span className="truncate">{storefrontUrl.replace(/^https?:\/\//, "")}</span>
                <ExternalLink className="w-3 h-3 shrink-0" />
              </a>
            )}
          </div>
        </div>

        {/* Scrollable menu content */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden scrollbar-none">
          <ErpSidebarContent />
        </div>

        {/* Collapse toggle at bottom of panel */}
        <div className="shrink-0 px-4 py-3 border-t border-gray-200/30 dark:border-gray-700/30">
          <button
            type="button"
            onClick={onToggleCollapse}
            className={cn(
              "flex items-center gap-2 px-2 py-1.5 rounded-sm w-full",
              "text-gray-400 hover:text-gray-600 hover:bg-gray-100/50",
              "transition-all duration-150 text-[12px]"
            )}
            aria-label={collapsed ? "ขยายเมนู" : "ย่อเมนู"}
          >
            <PanelLeftClose className="w-4 h-4 shrink-0" />
            <span>ย่อเมนู</span>
          </button>
        </div>
      </nav>
    </>
  );
}
